<template lang="pug">
component.button(
  :is="['link-white', 'link-black'].includes(color) ? 'a' : 'button'"
  :class="['link-white', 'link-black'].includes(color) ? [ color, 'link' ] : buttonClasses"
  :type="submit ? 'submit' : null"
  :disabled="disabled"
  ref="btn"
  :data-link="link"
  @click="click"
  @mouseenter="$emit('mouseenter', $event)"
  @mouseleave="$emit('mouseleave', $event)"
)
  Spinner.spin(v-if="loading" size="s" :color="iconFill")
  span.block.button-content
    Icon.icon.left.icon-size-m(v-if="leftIcon" :name="leftIcon" :fill="iconFill" )
    span.block.slot(:class="{'has-logo': logoIcon}")
      slot
        StaticImage.icon-logo(v-if="logos[logoIcon]" :class="['icon-'+logoIcon]" :src="logos[logoIcon]" :alt="text ? '' : logoIcon")
        | {{ __(text, 'auto') }}
    Icon.icon.right.icon-size-m(v-if="rightIcon" :name="rightIcon" :fill="iconFill" )
  slot(name="bottom")
</template>
<script setup lang="ts">
import { computed, inject, ref, useAttrs } from 'vue'
import { redirect } from '../../js/helpers'
import { useRogue } from '../../js/rogue'

type ButtonSize = 's' | 'm' | 'l'
type ButtonColor = 'flat' | 'gray' | 'black' | 'red' | 'green' | 'white' | 'link-white' | 'link-black' | 'link-blue' | 'black-outline' | 'white-outline' | 'wero-yellow' | 'trustly-green' | 'mobilepay-blue' | 'paysafecard-blue' | 'klarna-pink' | 'affirm-blue' | 'text-bold'
type LogoIcon = 'lyft' | 'uber' | 'google-maps' | 'facebook' | 'instagram'
type ButtonWidth = 'normal' | 'full' | 'half' | 'fixed'
type TextAlign = 'text-left' | 'text-right' | 'text-center'

const props = withDefaults(defineProps<{
  text?: string
  link?: string
  buttonType?: string
  action?: string
  value?: string
  newTab?: boolean
  /**
   * The size of the button. Defaults to medium.
   * `s, m, l`
   */
  size?: ButtonSize
  /**
   * Loading
   * `loading`
   */
  loading?: boolean
  /**
   * Click Events
   * `clickEvents`
   */
  clickEvents?: boolean
  /**
   * Disabled
   * `disabled`
   */
  disabled?: boolean
  /**
   * Color variation
   * `gray, black, red, green`
   */
  color?: ButtonColor
  /**
   * Icon name for left slot
   * `arrow-left`
   */
  leftIcon?: string | null
  /**
   * Icon name for right
   * `arrow-right`
   */
  rightIcon?: string | null
  logoIcon?: LogoIcon | null
  /**
   * Width
   * `normal, full, half`
   */
  width?: ButtonWidth
  /**
   * Sets the button to type submit
   */
  submit?: boolean
  stop?: boolean
  textAlign?: TextAlign
}>(), {
  text        : '',
  link        : '',
  buttonType  : 'link',
  action      : '',
  value       : '',
  newTab      : false,
  size        : 'm',
  loading     : false,
  clickEvents : true,
  disabled    : false,
  color       : 'gray',
  leftIcon    : null,
  rightIcon   : null,
  logoIcon    : null,
  width       : 'normal',
  submit      : false,
  stop        : true,
  textAlign   : 'text-center',
})

const emit = defineEmits<{
  mouseenter: [event: MouseEvent]
  mouseleave: [event: MouseEvent]
  click: [event: Event]
}>()

const attrs = useAttrs()
const rogue = useRogue()
const showOverlay = inject<(value: string) => void>('showOverlay', () => {})

// @TODO: Revisit later to improve where the logo is dynamically set
const logos: Record<LogoIcon, string> = {
  lyft          : '/Rogue%20Invitational/2021/Lyft_Icon_uo7blz.svg',
  uber          : '/Rogue%20Invitational/2021/Uber_Icon_y1jvhv.svg',
  'google-maps' : '/Rogue%20Invitational/2021/Google_Maps_Icon_k7tzap.svg',
  facebook      : '/Rogue%20Invitational/2021/facebook-squared_wcr9hs.svg',
  instagram     : '/Rogue%20Invitational/2021/instagram-outlined_kumxbj.svg',
}

const btn = ref<HTMLElement | null>(null)

const iconFill = computed(() => ['gray', 'white', 'flat'].indexOf(props.color) !== -1 ? 'black' : 'white')

const buttonClasses = computed(() => {
  const retVal: (string | Record<string, boolean>)[] = [
    `btn-size-${props.size}`,
    props.color,
    props.width,
    props.textAlign,
    {
      disabled : props.disabled,
      loading  : props.loading,
    },
  ]

  if (props.color === 'black-outline' || props.color === 'white-outline') {
    retVal.push('outline')
  }

  return retVal
})

const linkIsHash = computed(() => (props.link || '').indexOf('#') === 0 && props.link !== '#')

function click($event: Event) {
  if (props.stop && !linkIsHash.value) {
    $event.stopPropagation()
  }

  if (props.buttonType === 'action' && props.action && props.action === 'showOverlay' && props.value && showOverlay) {
    showOverlay(props.value)
  }
  if (!props.submit) {
    $event.preventDefault()

    // data-track is defined in RogueBuilder
    const trackingEvent = (attrs['data-track'] as string) || 'button_link_click'

    if (props.newTab && props.link) {
      if (rogue.appContext) {
        rogue.emitToApp?.('open_in_browser', { url: props.link })
      } else {
        window.open(props.link, '_blank')?.focus()
      }
      if (rogue) {
        rogue.track.track({ type: 'custom', event: trackingEvent, data: { link: props.link, text: props.text } })
      }
    } else if (props.buttonType === 'link' && props.link && !linkIsHash.value) {
      if (rogue) {
        rogue.track.track({ type: 'custom', event: trackingEvent, data: { link: props.link, text: props.text } })
      }
      redirect(props.link, rogue.router!, rogue)
    } else {
      emit('click', $event)
    }
    // Drop focus after mouse clicks only; keyboard activation (detail 0) must keep focus where the user is
    if (($event as MouseEvent).detail > 0) {
      btn.value?.blur()
    }
  }
}

defineExpose({
  btn,
})
</script>

<style lang="scss" scoped>
@use "sisu/styles/mixins" as *;

  .button {

    font-family: var(--btn-font-family, #{$font-base});
    font-size: var(--btn-font-size, #{$size-s});
    font-weight: var(--btn-font-weight, normal);
    text-transform: var(--btn-text-transform, none);
    line-height: $line-height-m;
    display: block;
    padding: var(--btn-padding, #{$size-xxs} #{$size-s});
    cursor: pointer;
    user-select: none;
    border: var(--btn-border, 0);
    border-radius: var(--btn-border-radius, 3px);
    background: var(--btn-background, inherit);
    color: var(--btn-color, inherit);
    box-shadow: var(--btn-box-shadow, none);
    width: var(--btn-width, auto);
    position: relative;

    &:focus {
      outline: 0;
    }

    &.link-white {
      color: $color-white;
    }

    &.link-black {
      color: $color-black;
    }
    &.link-blue {
      color: $color-blue;
      background-color: transparent;
      text-decoration: underline;
      font-size: $size-xs;
      padding: 0;
    }
    &.text-bold {
      color: $color-black;
      background-color: transparent;
      text-decoration: none;
      font-size: $size-xs;
      font-weight: bold;
      padding: 0;
    }
  }

  .button-content {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .slot {
    flex-grow: 1;
    letter-spacing: 0.1px;
    text-align: center;

    &.has-logo {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }

  // @TODO: Another improvement for later, allow styles to be set in RB
  .icon-logo {
    height: $size-xl;
    margin-right: $space-xxs;

    &.icon-lyft {
      width: 34px;
    }
    &.icon-uber, &.icon-facebook, &.icon-instagram {
      width: $size-xl;
    }
    &.icon-google-maps {
      width: 18px;
    }
  }

  .button.outline {
    background-color: transparent;
    border-radius: 0;
    font-weight: bold;
    text-transform: uppercase;
    text-decoration: none;
    font-family: 'Roboto Condensed';
  }

  .button.black-outline {
    color: $color-black;
    border: 3px solid $color-black;

    &:hover {
      color: $color-white;
      background-color: $color-black;
    }

    &.disabled {
      opacity: 1;
      color: $color-black;
    }
  }

  .button.white-outline {
    color: $color-white;
    border: 3px solid $color-white;

    &:hover {
      color: $color-black;
      background-color: $color-white;
    }

    &.disabled {
      opacity: 1;
      color: $color-white;
    }
  }

  .no-click {
    pointer-events: none;
  }

  // Sizes - use CSS variables for customizable padding and font-size
  .btn-size-l {
    --btn-padding: #{$size-s} #{$size-m};
    --btn-font-size: #{$size-m};
    font-size: var(--btn-font-size);
    padding: var(--btn-padding);
  }

  .btn-size-m {
    --btn-padding: #{$size-xs} #{$size-s};
    --btn-font-size: #{$size-s};
    font-size: var(--btn-font-size);
    padding: var(--btn-padding);
  }

  .btn-size-s {
    --btn-padding: #{$size-xxs} #{$size-xs};
    --btn-font-size: #{$size-xs};
    font-size: var(--btn-font-size);
    padding: var(--btn-padding);
  }

  .btn-size-xs {
    --btn-padding: #{$size-xxs} #{$size-xs};
    --btn-font-size: #{$size-xxs};
    font-size: var(--btn-font-size);
    padding: var(--btn-padding);
  }

  // Width
  .half {
    width: 50%;
  }

  .full {
    width: 100%;
  }

  // Flat button
  .flat {
    border-radius: 1px;
    color: $color-black;
    border: $border-width-thin solid $color-gray;
    background-color: $color-white;

    &:hover {
      border-color: $color-gray-dark;
      border-width: $border-width-medium;
    }

    &:active {
      border-color: $color-black;
    }

    &:focus {
      outline: 2px solid $color-gray;
      outline-offset: 2px;
    }

    &.selected {
      background-color: black;
      color: white;
    }
  }

  // Flat button
  .flat-disabled {
    border-radius: $radius-default;
    pointer-events: none;
    background-color: $color-gray-light;
    color: $color-gray-dark;
  }

  // Gray button
  .gray {
    @include btn-theme(
        $background: linear-gradient(180deg, $color-white 0%, $color-gray-light 100%),
        $color: $color-black,
        $border: $color-gray,
        $focusBackground: $color-gray,
        $hoverBackground: $color-gray-light,
        $activeColor: $color-gray-light,
        $activeBackground: $color-gray,
    )
  }

  .black {
    @include btn-theme(
        $background: linear-gradient(180deg, $color-black-light 0%, $color-black 100%),
        $color: $color-white,
        $border: null,
        $focusBackground: $color-black,
        $hoverBackground: $color-black-light,
        $activeColor: $color-gray-dark,
        $activeBackground: $color-black,
    )
  }

  .red {
    @include btn-theme(
        $background: linear-gradient(180deg, $color-red 0%, $color-red-dark 100%),
        $color: $color-white,
        $border: null,
        $focusBackground: $color-red-dark,
        $hoverBackground: linear-gradient(180deg, $color-red-light 0%, $color-red 100%),
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-red-dark,
    )
  }

  .wero-yellow {
    @include btn-theme(
        $background: $color-wero-yellow,
        $color: $color-black,
        $border: null,
        $focusBackground: $color-wero-yellow,
        $hoverBackground: $color-wero-yellow,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-wero-yellow,
    )
  }

  .trustly-green {
    @include btn-theme(
        $background: $color-trustly,
        $color: $color-white,
        $border: null,
        $focusBackground: $color-trustly-light,
        $hoverBackground: $color-trustly-light,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-trustly-light,
    )
  }

  .mobilepay-blue {
    @include btn-theme(
        $background: $color-mobilepay-blue,
        $color: $color-white,
        $border: null,
        $focusBackground: $color-mobilepay-blue,
        $hoverBackground: $color-mobilepay-blue,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-mobilepay-blue,
    )
  }

  .paysafecard-blue {
    @include btn-theme(
        $background: $color-paysafecard-blue,
        $color: $color-white,
        $border: null,
        $focusBackground: $color-paysafecard-blue,
        $hoverBackground: $color-paysafecard-blue,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-paysafecard-blue,
    )
  }

  .affirm-blue {
    @include btn-theme(
        $background: $color-affirm-blue,
        $color: $color-white,
        $border: null,
        $focusBackground: $color-affirm-blue,
        $hoverBackground: $color-affirm-blue,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-affirm-blue,
    )
  }

  .klarna-pink {
    @include btn-theme(
      $background: $color-klarna-pink,
      $color: $color-black,
      $border: null,
      $focusBackground: $color-klarna-pink,
      $hoverBackground: $color-klarna-pink,
      $activeColor: rgba($color-black, 0.5),
      $activeBackground: $color-klarna-pink,
    )
  }

  .green {
    @include btn-theme(
        $background: linear-gradient(180deg, $color-green 0%, $color-green-dark 100%),
        $color: $color-white,
        $border: null,
        $focusBackground: $color-green-dark,
        $hoverBackground: linear-gradient(180deg, $color-green-light 0%, $color-green 100%),
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-green-dark,
    )
  }

  .white {
    @include btn-theme(
        $background: $color-white,
        $color: $color-black,
        $border: $color-gray-light,
        $focusBackground: $color-gray-light,
        $hoverBackground: $color-gray-light,
        $activeColor: rgba($color-black, 0.5),
        $activeBackground: $color-gray-x-light,
    )
  }

  .button.disabled {
    cursor: default;
    opacity: .4;
    color: $color-gray;
    pointer-events: none;
  }

  .text-left .slot {
    text-align: left;
  }

  .text-right .slot {
    text-align: right;
  }

  .text-center .slot {
    text-align: center;
  }

  .spin {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -12px;
    margin-left: -12px;
  }

  //loading
  .button.loading {
    cursor: default;
    pointer-events: none;

    .button-content {
      visibility: hidden;
    }
  }

  .left.icon {
    padding-right: $space-xxs;
  }

  .right.icon {
    padding-left: $space-xxs;
  }
</style>

<style lang="scss" scoped target="app">
  .platform-ios, .platform-android {
    .button:not(.decrement):not(.increment):not(.ctrl) {
      min-height: $app-button-height;

      &.fixed {
        border-radius: 0;
        width: 100%;
      }

      &:not(:last-child) {
        margin-bottom: $app-gap-default;

        &.fixed {
          margin-bottom: $app-gap-default * 0.25;
        }
      }

      &, .text-style {
        font-family: $font-condensed;
        font-weight: bold;
        text-transform: uppercase;
      }
    }
  }
</style>

<style lang="scss">
  @use 'sass:color';

  /* Dark mode styles for buttons */
  [data-theme='dark'] .button.gray,
  .dark-mode .button.gray {
    background: $color-black-light;
    border-color: $color-black-light;
    color: $color-white;

    &:hover {
      background: color.adjust($color-black-light, $lightness: 5%);
    }

    &:active {
      background: $color-black-light;
    }
  }
</style>
